import type { Middleware } from "@reduxjs/toolkit";
import { resetSlugResolutionState } from "../../router/slugResolutionState";
import { setAnalyticsActive } from "../analytics/analyticsActive";
import { clearCompanySelectionDedup } from "../analytics/companySelection";
import { getPostHog } from "../analytics/posthog";
import {
  pushSessionClearToExtension,
  pushSessionToExtension,
  pushStoredSessionToExtension,
} from "./extensionSessionPush";
import { clearActiveLinePreferences } from "./phoneActiveLinePreferences";
import {
  authenticatedSessionUserId,
  type SessionState,
  sessionSlice,
} from "./session";
import { hasSession } from "./sessionCacheMiddleware";
import {
  clearPersistedSession,
  persistCompanyId,
  persistInteractiveLogin,
  persistTokenRefresh,
  persistUser,
} from "./sessionPersistence";

interface SessionLifecycleRootState {
  session: SessionState;
}

export type SessionLifecycleDependencies = {
  clearActiveLinePreferences: typeof clearActiveLinePreferences;
  clearCompanySelectionDedup: typeof clearCompanySelectionDedup;
  getPostHog: () => Pick<
    NonNullable<ReturnType<typeof getPostHog>>,
    "opt_out_capturing" | "reset"
  > | null;
  pushSessionClearToExtension: typeof pushSessionClearToExtension;
  pushSessionToExtension: typeof pushSessionToExtension;
  pushStoredSessionToExtension: typeof pushStoredSessionToExtension;
  resetSlugResolutionState: typeof resetSlugResolutionState;
  setAnalyticsActive: typeof setAnalyticsActive;
};

const defaultSessionLifecycleDependencies: SessionLifecycleDependencies = {
  clearActiveLinePreferences,
  clearCompanySelectionDedup,
  getPostHog,
  pushSessionClearToExtension,
  pushSessionToExtension,
  pushStoredSessionToExtension,
  resetSlugResolutionState,
  setAnalyticsActive,
};

export class SessionPersistenceError extends Error {
  constructor(transition: string) {
    super(`Unable to persist session ${transition}.`);
    this.name = "SessionPersistenceError";
  }
}

const requirePersistence = (persisted: boolean, transition: string): void => {
  if (!persisted) {
    throw new SessionPersistenceError(transition);
  }
};

/**
 * Own browser and integration effects caused by session transitions. Reducers
 * stay deterministic; effects still finish synchronously before dispatch
 * returns, including router-driven logout paths outside React.
 */
export const createSessionLifecycleMiddleware =
  ({
    clearActiveLinePreferences,
    clearCompanySelectionDedup,
    getPostHog,
    pushSessionClearToExtension,
    pushSessionToExtension,
    pushStoredSessionToExtension,
    resetSlugResolutionState,
    setAnalyticsActive,
  }: SessionLifecycleDependencies = defaultSessionLifecycleDependencies): Middleware<
    unknown,
    SessionLifecycleRootState
  > =>
  ({ getState }) =>
  (next) =>
  (action) => {
    const previousSession = getState().session;

    if (sessionSlice.actions.login.match(action)) {
      requirePersistence(persistInteractiveLogin(action.payload), "login");
      // An explicit login always begins a new caller-ID preference lifetime,
      // even when an earlier browser session ended before it could log out.
      // Do this only after the session itself persists, so a failed login
      // write cannot erase a still-valid preference.
      clearActiveLinePreferences(action.payload.user.id);
      const result = next(action);
      pushSessionToExtension(action.payload);
      return result;
    }

    if (sessionSlice.actions.tokenRefreshed.match(action)) {
      requirePersistence(persistTokenRefresh(action.payload), "token refresh");
      const result = next(action);
      pushSessionToExtension(action.payload);
      return result;
    }

    if (sessionSlice.actions.setCompanyId.match(action)) {
      requirePersistence(persistCompanyId(action.payload), "company change");
      const result = next(action);
      pushStoredSessionToExtension();
      return result;
    }

    if (sessionSlice.actions.setUser.match(action)) {
      requirePersistence(persistUser(action.payload), "user update");
      return next(action);
    }

    if (sessionSlice.actions.syncAuthFromStorage.match(action)) {
      const previousUserId = authenticatedSessionUserId(previousSession);
      const nextUserId = action.payload.user?.id ?? null;
      // Another tab's logout reaches this tab through the auth storage event.
      // Clear the outgoing actor before the reducer drops it; a same-user token
      // refresh intentionally keeps the preference intact.
      if (previousUserId && action.payload.logoutObserved) {
        // Session storage is private to this tab, so a delayed logout can only
        // clear this tab's old choice. It cannot erase a fresh choice selected
        // in the tab that performed a same-user re-login.
        clearActiveLinePreferences(previousUserId);
      } else if (
        previousUserId &&
        (!action.payload.accessToken || nextUserId !== previousUserId)
      ) {
        clearActiveLinePreferences(previousUserId);
      }
    }

    if (sessionSlice.actions.logout.match(action)) {
      // Logout remains fail-closed even if storage cannot be cleared. An
      // explicit logout writes its sticky marker before dispatch; an expired
      // token remains expired. Never retain an authenticated Redux session.
      clearActiveLinePreferences(authenticatedSessionUserId(previousSession));
      clearPersistedSession();
    }

    const result = next(action);

    if (sessionSlice.actions.logout.match(action)) {
      resetSlugResolutionState();
      pushSessionClearToExtension();
      // Unconditional: a tracked view still mounted during logout must not
      // fire against the outgoing identity.
      setAnalyticsActive(false);

      const posthog = getPostHog();
      if (hasSession(previousSession) && posthog) {
        posthog.reset();
        clearCompanySelectionDedup();
        posthog.opt_out_capturing();
      }
      return result;
    }

    return result;
  };
